import React from 'react';
import { Link } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

function Section({ title, count, countLabel, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {typeof count === 'number' && (
          <span className="ml-auto text-sm text-gray-400">
            {count} {count === 1 ? countLabel : `${countLabel}s`}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-gray-500 py-6 text-center">{children}</p>;
}

function Field({ label, value, mono }) {
  const missing = value === null || value === undefined || value === '';
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className={`mt-0.5 text-sm ${missing ? 'text-gray-400 italic' : 'text-gray-900'} ${mono && !missing ? 'font-mono text-xs break-all' : ''}`}>
        {missing ? 'Not set' : value}
      </dd>
    </div>
  );
}

function Pill({ tone, children }) {
  const palette = {
    green: 'bg-green-50 text-green-700 border-green-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${palette[tone] || palette.gray}`}>
      {children}
    </span>
  );
}

// A checkout the buyer walked away from is still a stored record, but it is not
// a purchase — these labels are what keep the two apart on this page.
const PURCHASE_STATES = {
  abandoned: { label: 'Abandoned checkout', tone: 'gray' },
  completed: { label: 'Completed', tone: 'green' },
  expired: { label: 'Expired', tone: 'gray' },
  in_progress: { label: 'Checkout in progress', tone: 'amber' },
  paid: { label: 'Paid · account not created', tone: 'indigo' },
  superseded: { label: 'Superseded attempt', tone: 'gray' },
};

export default function SuperadminUserDetail({
  user,
  facilities = [],
  licenses = [],
  purchases = [],
  cohorts = [],
  webinars = [],
  emails = [],
  emails_total: emailsTotal = 0,
  emails_truncated: emailsTruncated = false,
}) {
  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <Link
            href="/superadmin/users"
            className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            All users
          </Link>
          <div className="mt-3 flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Superadmin
          </div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">{user.display_name}</h1>
          <p className="mt-1 text-sm text-gray-500">
            <span className="font-mono text-xs text-gray-400">#{user.id}</span>
            <span className="mx-1.5">·</span>
            {user.email}
          </p>
        </div>

        <Section title="Identity and account">
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
            <Field label="User ID" value={user.id} mono />
            <Field label="First name" value={user.first_name} />
            <Field label="Last name" value={user.last_name} />
            <Field label="Email" value={user.email} />
            <Field label="Role" value={user.role} />
            <Field label="User type" value={user.user_type} />
            <Field label="Student type" value={user.student_type} />
            <Field label="Training type" value={user.training_type} />
            <Field label="Certification type" value={user.certification_type} />
            <Field label="Facility preference" value={user.facility_type_preference} />
            <Field label="License number" value={user.license_number} mono />
            <Field label="License expiration" value={user.license_expiration_date} />
            <Field
              label="Email verified"
              value={user.email_verified
                ? <Pill tone="green">Verified {user.email_verified_at}</Pill>
                : <Pill tone="amber">Not verified</Pill>}
            />
            <Field label="Joined" value={user.created_at} />
            <Field label="LearnWorlds user ID" value={user.learn_worlds_user_id} mono />
            <Field label="Stripe customer ID" value={user.stripe_customer_id} mono />
          </dl>

          <div className="mt-6 pt-5 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Facility memberships</h3>
            {facilities.length === 0 ? (
              <p className="text-sm text-gray-500">This user does not belong to a facility.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {facilities.map((facility) => (
                  <div key={facility.id} className="py-2.5 flex items-center gap-2 first:pt-0 last:pb-0">
                    <p className="font-medium text-gray-900">{facility.name || 'Unnamed facility'}</p>
                    <Pill tone="indigo">{facility.role}</Pill>
                    <span className="ml-auto text-xs text-gray-400 font-mono">#{facility.facility_id}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        <Section title="Licenses and billing" count={licenses.length} countLabel="license">
          {licenses.length === 0 ? (
            <Empty>No licenses have been purchased by or assigned to this user.</Empty>
          ) : (
            <div className="divide-y divide-gray-100">
              {licenses.map((license) => (
                <div key={license.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900">{license.name || 'Unknown license'}</p>
                    {license.active
                      ? <Pill tone="green">Active</Pill>
                      : <Pill tone={license.expired ? 'rose' : 'gray'}>{license.status || 'Unknown status'}</Pill>}
                    <Pill tone={license.source === 'self_purchased' ? 'indigo' : 'gray'}>
                      {license.source === 'self_purchased' ? 'Self-purchased' : 'Facility-assigned'}
                    </Pill>
                  </div>
                  <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
                    <Field label="License type" value={license.license_type} />
                    <Field label="Billing model" value={license.billing_model} />
                    <Field label="Billing frequency" value={license.frequency} />
                    <Field label="Student / facility type" value={[license.student_type, license.facility_type].filter(Boolean).join(' · ')} />
                    <Field label="Assigned" value={license.assigned_at} />
                    <Field label="Expires" value={license.expires_at} />
                    <Field label="Facility" value={license.facility_name} />
                    <Field label="Stripe line item" value={license.stripe_line_item_id} mono />
                  </dl>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Purchases" count={purchases.length} countLabel="purchase">
          {purchases.length === 0 ? (
            <Empty>No purchase records are stored for this email address.</Empty>
          ) : (
            <div className="divide-y divide-gray-100">
              {purchases.map((purchase) => (
                <div key={purchase.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900">{purchase.items_summary || 'No line items recorded'}</p>
                    <Pill tone={PURCHASE_STATES[purchase.state]?.tone || 'gray'}>
                      {PURCHASE_STATES[purchase.state]?.label || purchase.status}
                    </Pill>
                  </div>
                  <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
                    <Field label="Buyer type" value={purchase.buyer_type} />
                    <Field label="Training type" value={purchase.training_type} />
                    <Field label="Facility type" value={purchase.facility_type} />
                    <Field label="Cohort month" value={purchase.cohort_month} />
                    <Field label="Created" value={purchase.created_at} />
                    <Field label="Paid" value={purchase.paid_at} />
                    <Field label="Stripe payment intent" value={purchase.stripe_payment_intent_id} mono />
                    <Field label="Stripe subscription" value={purchase.stripe_subscription_id} mono />
                  </dl>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Cohort" count={cohorts.length} countLabel="record">
          {cohorts.length === 0 ? (
            <Empty>No cohort month is recorded for this user. Only live-cohort purchases collect one.</Empty>
          ) : (
            <div className="divide-y divide-gray-100">
              {cohorts.map((cohort) => (
                <div key={cohort.cohort_month} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-gray-900">{cohort.cohort_month}</p>
                      {!cohort.confirmed && <Pill tone="amber">Unpaid attempt</Pill>}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {cohort.source}
                      {cohort.detail && ` · ${cohort.detail}`}
                      {cohort.attempts > 1 && ` · ${cohort.attempts} records`}
                    </p>
                  </div>
                  <span className="sm:ml-auto text-xs text-gray-400">Recorded {cohort.recorded_at}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Registered webinars" count={webinars.length} countLabel="registration">
          {webinars.length === 0 ? (
            <Empty>This user has not registered for any webinars.</Empty>
          ) : (
            <div className="divide-y divide-gray-100">
              {webinars.map((registration) => (
                <div key={registration.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900">{registration.title}</p>
                    <Pill tone={registration.registration_status === 'cancelled' ? 'rose' : 'green'}>
                      {registration.registration_status}
                    </Pill>
                    <Pill tone={registration.attended ? 'green' : 'gray'}>
                      {registration.attended ? 'Attended' : 'Not attended'}
                    </Pill>
                    {registration.credit_awarded && <Pill tone="indigo">Credit awarded</Pill>}
                  </div>
                  <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
                    <Field label="Starts" value={registration.starts_at ? `${registration.starts_at} (${registration.time_zone})` : null} />
                    <Field label="Ends" value={registration.ends_at} />
                    <Field label="Webinar status" value={registration.webinar_status} />
                    <Field label="Credit hours" value={registration.credit_hours} />
                    <Field label="Registered" value={registration.registered_at} />
                    <Field label="Cancelled" value={registration.cancelled_at} />
                    <Field
                      label="Attendance minutes"
                      value={registration.attendance_minutes === null || registration.attendance_minutes === undefined
                        ? null
                        : `${registration.attendance_minutes} min`}
                    />
                    <Field label="Credit awarded" value={registration.credit_awarded_at} />
                  </dl>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Email timeline" count={emailsTotal} countLabel="email">
          {emails.length === 0 ? (
            <Empty>No tracked emails have been sent to this user yet.</Empty>
          ) : (
            <>
              {emailsTruncated && (
                <p className="text-xs text-gray-400 mb-3">Showing the {emails.length} most recent of {emailsTotal}.</p>
              )}
              <div className="divide-y divide-gray-100">
                {emails.map((email) => (
                  <div key={email.id} className="py-3 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">{email.subject || 'No subject'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {email.mailer || 'Unknown mailer'}
                        <span className="mx-1.5">·</span>
                        {email.to_address}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{email.sent_at}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Section>
      </div>
    </Layout>
  );
}
